import { dbKeyMap } from './dbConfig';
import type { SchoolWithKey } from '../types';

export type SchoolSelectorSchoolMeta = {
    schoolName?: { el: string; en: string };
    schoolDomain?: string;
    schoolLandingCamp?: boolean;
};

export function schoolSelectorEligibleKeys(
    schoolGroup: Record<string, unknown> | null,
    schoolLandingHide?: Record<string, boolean> | null
): string[] {
    if (!schoolGroup) return [];
    let keys = Object.keys(schoolGroup).filter(key => schoolGroup[key] === true);
    if (schoolLandingHide) {
        keys = keys.filter(key => schoolLandingHide[key] !== true);
    }
    return keys;
}

export function isSchoolLandingCamp(
    allSchools: Record<string, SchoolSelectorSchoolMeta> | null,
    schoolKey: string
): boolean {
    return allSchools?.[schoolKey]?.schoolLandingCamp === true;
}

export type SchoolSelectorResources = {
    schoolGroup: Record<string, unknown> | null;
    /** Present only when `schoolLandingHide/{groupKey}` exists in RTDB. */
    schoolLandingHide?: Record<string, boolean> | null;
    allSchools: Record<string, SchoolSelectorSchoolMeta> | null;
};

export type BuildSchoolSelectorOptions = {
    /** `school` = regular landing section; `camp` = summer camp section only. */
    landingSection?: 'school' | 'camp';
};

export function buildSchoolSelectorSchools(
    resources: SchoolSelectorResources,
    options?: BuildSchoolSelectorOptions
): SchoolWithKey[] {
    const { schoolGroup, schoolLandingHide, allSchools } = resources;
    const schoolKeys = schoolSelectorEligibleKeys(schoolGroup, schoolLandingHide);
    if (!schoolKeys.length || !allSchools) return [];

    return schoolKeys
        .map(schoolKey => {
            const school = allSchools[schoolKey];
            const isCamp = isSchoolLandingCamp(allSchools, schoolKey);
            if (options?.landingSection === 'school' && isCamp) return null;
            if (options?.landingSection === 'camp' && !isCamp) return null;
            const schoolDomain = school?.schoolDomain || dbKeyMap[schoolKey];
            if (school?.schoolName && schoolDomain) {
                return {
                    key: schoolKey,
                    schoolName: school.schoolName,
                    schoolDomain
                };
            }
            return null;
        })
        .filter((school): school is SchoolWithKey => school !== null);
}
